import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus as PlusIcon, ShieldCheck, Clock } from "lucide-react";
import { api } from "@/lib/api";
import TabShell from "./TabShell";

// Guarantee copy mirrors the existing "Refunds & guarantees" accordion in
// ProductPage: same-day replacement / full refund / pre-dispatch cancel.
//
// The trust strip at the bottom of the panel is now COA-aware: it shows
// VERIFIED with a link to the current batch report ONLY when an
// independent COA has been published. Pending / in-progress products
// fall back to a neutral state that links to the COA library entry —
// no purity claims, no "every lot" language.
export default function VerifiedPanel({ productSlug }) {
  const { data } = useQuery({
    queryKey: ["coa", productSlug],
    queryFn: async () => (await api.get(`/coa/product/${productSlug}`)).data,
    enabled: !!productSlug,
    staleTime: 60_000,
  });
  const current = data?.current;
  const guarantees = [
    { n: "01", t: "Same-day replacement", d: "On damaged or lost shipments." },
    { n: "02", t: "Full-refund alternative", d: "On request, in lieu of a replacement." },
    { n: "03", t: "Pre-dispatch cancellation", d: "Anytime before the parcel leaves us." },
  ];
  return (
    <TabShell testId="verified-panel">
      <div className="mx-auto flex w-full max-w-[580px] flex-col">
        <p className="font-mono text-[10px] tracking-[0.18em] text-subtle sm:text-[11px] sm:tracking-[0.2em]">
          § AUTHORIZED DISTRIBUTOR
        </p>
        <h3
          className="mt-1 font-heading text-base font-black leading-tight text-ink sm:mt-2 sm:text-2xl"
          data-testid="verified-panel-headline"
        >
          Two companies. <span className="text-brandblue">One standard.</span>
        </h3>

        {/* Two-column trust panel with a centered "+" divider marker. */}
        <div className="relative mt-3 grid grid-cols-2 gap-2.5 sm:mt-5 sm:gap-4">
          <div
            className="rounded-lg border border-line bg-white/80 p-3 sm:p-4"
            data-testid="verified-manufacturer"
          >
            <p className="font-mono text-[10px] tracking-[0.14em] text-subtle">MANUFACTURER</p>
            <img
              src="/logos/trenta-medical.png"
              alt="Trenta Medical"
              className="mt-1 h-6 w-auto max-w-full object-contain object-left sm:mt-1.5 sm:h-7"
              data-testid="verified-manufacturer-logo"
              draggable={false}
            />
            <ul className="mt-2 space-y-1 text-[11px] leading-snug text-subtle sm:mt-3 sm:space-y-1.5 sm:text-[12px] sm:leading-relaxed">
              <li>• UK/EU facility</li>
              <li>• Nitrogen-sealed</li>
              <li>• Synthesises every compound</li>
            </ul>
          </div>
          {/* Centered "+" marker between the two horizontally-placed cards. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-brandblue shadow-sm sm:h-7 sm:w-7"
          >
            <PlusIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={2.5} />
          </span>
          <div
            className="rounded-lg border border-line bg-white/80 p-3 sm:p-4"
            data-testid="verified-distributor"
          >
            <p className="font-mono text-[10px] tracking-[0.14em] text-subtle">AUTHORIZED DISTRIBUTOR</p>
            <img
              src="/logos/pepdirect-brand.png"
              alt="PepDirect"
              className="mt-1 h-6 w-auto max-w-full object-contain object-left sm:mt-1.5 sm:h-7"
              data-testid="verified-distributor-logo"
              draggable={false}
            />
            <ul className="mt-2 space-y-1 text-[11px] leading-snug text-subtle sm:mt-3 sm:space-y-1.5 sm:text-[12px] sm:leading-relaxed">
              <li>• UK & EU dispatch</li>
              <li>• Tracked shipping</li>
              <li>• Public COA library</li>
            </ul>
          </div>
        </div>

        {/* COA state strip — VERIFIED only when a tested/published batch
            exists; otherwise a neutral pending / in-progress state that
            still links to the COA library so the customer can see the
            current status and get in touch. */}
        <CoaStrip current={current} productSlug={productSlug} />

        {/* Three guarantees, 01 / 02 / 03. Numbers in brand blue. */}
        <div className="mt-3 sm:mt-6">
          <p className="font-mono text-[10px] tracking-[0.18em] text-subtle sm:tracking-[0.2em]">
            § THREE GUARANTEES
          </p>
          <div
            className="mt-2 grid grid-cols-3 gap-1.5 sm:mt-3 sm:gap-2.5"
            data-testid="verified-guarantees"
          >
            {guarantees.map((g) => (
              <div
                key={g.n}
                className="rounded-md border border-line bg-white/80 p-2 sm:p-3"
                data-testid={`verified-guarantee-${g.n}`}
              >
                <p className="font-heading text-[11px] font-black text-brandblue sm:text-[13px]">{g.n}</p>
                <p className="mt-0.5 font-heading text-[11px] font-bold leading-tight text-ink sm:mt-1 sm:text-[12px]">
                  {g.t}
                </p>
                <p className="mt-0.5 text-[10px] leading-tight text-subtle sm:mt-1 sm:text-[11px] sm:leading-snug">{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </TabShell>
  );
}

function CoaStrip({ current, productSlug }) {
  const status = current?.testing_status;
  const anchor = current?.lot_number
    ? `/coa#lot-${current.lot_number}`
    : `/coa#pending-${productSlug}`;
  const reportHref = current?.coa_file_url || current?.verification_url;

  let heading;
  let icon;
  let body;
  let primaryLink;
  let primaryLabel;

  if (status === "tested") {
    heading = "INDEPENDENTLY TESTED";
    icon = "✓";
    body = "Independent laboratory results are available for this lot.";
    primaryLink = reportHref || anchor;
    primaryLabel = "View COA →";
  } else if (status === "in_progress") {
    heading = "INDEPENDENT TESTING IN PROGRESS";
    icon = "◷";
    body = "Independent laboratory testing for this lot is currently in progress.";
    primaryLink = "/contact";
    primaryLabel = "Contact us →";
  } else {
    heading = "INDEPENDENT COA PENDING";
    icon = "◷";
    body = "This lot has not yet completed independent laboratory testing.";
    primaryLink = "/contact";
    primaryLabel = "Contact us →";
  }

  const primaryIsExternal = primaryLink.startsWith("http");
  const primaryProps = primaryIsExternal
    ? { href: primaryLink, target: "_blank", rel: "noopener noreferrer" }
    : { to: primaryLink };
  const PrimaryCmp = primaryIsExternal ? "a" : Link;

  const testId =
    status === "tested"
      ? "verified-lab-strip-tested"
      : status === "in_progress"
      ? "verified-lab-strip-in-progress"
      : "verified-lab-strip-pending";

  return (
    <div
      className="mt-3 rounded-lg border border-brandblue/15 bg-brandbluelight/60 px-3 py-2.5 sm:mt-5 sm:px-4 sm:py-3"
      data-testid={testId}
    >
      <p
        className="flex items-center gap-1.5 font-mono text-[12px] font-semibold tracking-[0.14em] text-brandblue sm:text-[13px]"
        data-testid="verified-lab-line"
      >
        <span aria-hidden="true">{icon}</span> {heading}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-subtle sm:text-[12px] sm:leading-relaxed">
        {body}
      </p>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold">
        <PrimaryCmp
          {...primaryProps}
          className="text-brandblue hover:underline"
          data-testid="verified-lab-primary-link"
        >
          {primaryLabel}
        </PrimaryCmp>
        <span aria-hidden="true" className="text-subtle/60">·</span>
        <Link
          to={anchor}
          className="text-brandblue hover:underline"
          data-testid="verified-lab-library-link"
        >
          View COA Library →
        </Link>
      </p>
    </div>
  );
}
